import type { CohortConditionGroup, CohortRuleConditions } from "@unbox/api";
import { useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";
import { isConditionComplete, makeDefaultCondition } from "./condition-fields";

// No zodResolver here, unlike the other forms in this app. `cohortRuleConditionsSchema`
// is a zod-v4 schema and this package resolves `zod/v4` to its own 4.1.x install, while
// @hookform/resolvers type-checks against the hoisted zod's v4 core (4.0.x). The two
// disagree on an internal version brand, so the schema cannot be passed to the resolver
// without discarding its types. The server re-validates with that same schema on every
// write, and `canSave` below covers what the user can actually get wrong here.
export type RuleDraftValues = {
  name: string;
  cohortId: string;
  conditions: CohortRuleConditions;
  isActive: boolean;
  unassignAlreadyAssigned: boolean;
};

export type RuleDraftSource = {
  name: string;
  cohortId: string;
  conditions: CohortRuleConditions;
  isActive: boolean;
};

export type RuleDraftField = "name" | "when" | "except" | "cohortId";

// Every group must hold at least one condition and every condition a usable value,
// otherwise the draft would be sent as a rule that matches nothing.
const isGroupComplete = (group: CohortConditionGroup) =>
  group.conditions.length > 0 && group.conditions.every(isConditionComplete);

export const makeConditionGroup = (): CohortConditionGroup => ({
  logic: "all",
  conditions: [makeDefaultCondition()],
});

const emptyDraft = (): RuleDraftValues => ({
  name: "",
  cohortId: "",
  conditions: {
    version: 1,
    when: [makeConditionGroup()],
    except: [],
  },
  isActive: true,
  unassignAlreadyAssigned: false,
});

const draftFromRule = (rule: RuleDraftSource): RuleDraftValues => ({
  name: rule.name,
  cohortId: rule.cohortId,
  conditions: {
    ...rule.conditions,
    when: rule.conditions.when.length > 0 ? rule.conditions.when : [makeConditionGroup()],
  },
  isActive: rule.isActive,
  unassignAlreadyAssigned: false,
});

export const toCreateRulePayload = (values: RuleDraftValues) => ({
  name: values.name,
  cohortId: values.cohortId,
  conditions: values.conditions,
});

export const toUpdateRulePayload = (values: RuleDraftValues) => ({
  name: values.name,
  conditions: values.conditions,
  isActive: values.isActive,
  unassignAlreadyAssigned: values.unassignAlreadyAssigned,
});

export const useRuleDraft = () => {
  const form = useForm<RuleDraftValues>({
    defaultValues: emptyDraft(),
    mode: "onChange",
  });

  const conditions = useWatch({ control: form.control, name: "conditions" });
  const name = useWatch({ control: form.control, name: "name" });
  const cohortId = useWatch({ control: form.control, name: "cohortId" });

  const hydrate = useCallback(
    (rule?: RuleDraftSource) => form.reset(rule ? draftFromRule(rule) : emptyDraft()),
    [form],
  );

  const exceptGroup = conditions?.except[0] ?? null;

  const setExceptGroup = useCallback(
    (group: CohortConditionGroup | null) => {
      form.setValue("conditions.except", group ? [group] : [], { shouldValidate: true, shouldDirty: true });
    },
    [form],
  );

  const missingField: RuleDraftField | null =
    name.trim().length === 0
      ? "name"
      : !conditions || conditions.when.length === 0 || !conditions.when.every(isGroupComplete)
        ? "when"
        : !conditions.except.every(isGroupComplete)
          ? "except"
          : cohortId.length === 0
            ? "cohortId"
            : null;

  return {
    form,
    conditions,
    exceptGroup,
    setExceptGroup,
    hydrate,
    missingField,
    canSave: missingField === null,
  };
};
